import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";
import { useState } from "react";

export interface CheckboxProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  value?: boolean;
  onClick: React.MouseEventHandler<HTMLInputElement>;
  id: string;
  label: string;
  boldLabel?: string;
  labelClassName?: string;
  boldLabelClassName?: string;
  checkboxClassName?: string;
  tooltipChild?: React.ReactNode;
  showError?: boolean;
  errorMessage?: string;
}

const Checkbox = React.forwardRef<HTMLDivElement, CheckboxProps>(
  (
    {
      className,
      asChild = false,
      value,
      onClick,
      id,
      label,
      boldLabel,
      labelClassName,
      boldLabelClassName,
      checkboxClassName,
      tooltipChild = <div></div>,
      children,
      showError = false,
      errorMessage = "This field is required",
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "div";
    const [isChecked, setIsChecked]= useState<boolean>(false);
    return (
      <Comp
        className={cn("font-bold flex flex-row items-center md:ml-2", className)}
        ref={ref}
        {...props}
      >
        <input
          type="checkbox"
          id={id}
          className={cn(
            "appearance-none relative peer shrink-0 w-4 h-4 border-[1px] border-gray-300 focus:border-green-600 rounded-sm bg-transparent checked:bg-green-600 checked:border-0",
            checkboxClassName
          )}
          checked={value}
          onChange={(e) =>{ setIsChecked(e as any) ;return onClick(e as any);}}
        />
        <label
          htmlFor={id}
          className={cn(
            "ml-2 text-xs font-light text-gray-600",
            labelClassName
          )}
        >
          {label} <span className={cn("font-bold", boldLabelClassName)}>{boldLabel ?? ""}</span>{" "}
          {tooltipChild}
        </label>
        <svg
          className={cn(
            "absolute w-4 h-4 text-white hidden peer-checked:block pointer-events-none p-[4px]",
            checkboxClassName
          )}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
        <div
          className={`text-xs ${
            showError ? "text-red-500" : "text-transparent"
          } font-medium flex flex-row items-center space-x-1 mt-1`}
        >
          {showError && <span>{errorMessage}</span>}
        </div>
      </Comp>
    );
  }
);

Checkbox.displayName = "Checkbox";

export { Checkbox };
